import React from "react";
import { Card, Button } from "components/ui";
import { DatePicker } from "components/shared/form/Datepicker";
import { TimePicker } from "components/shared/form/Timepicker";
import {
  CalendarDaysIcon,
  BellIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { formatDate } from "utils/dateUtils";
import { Input, Select } from "components/ui/Form";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { MultiSelectHierarchicalFilterDropdown } from "../filters/MultiSelectHierarchicalFilterDropdown";
import { ACTIVITY_STATUS } from "constants/activityStatus.constant";
import { Listbox } from "components/shared/form/Listbox";
import { Combobox } from "components/shared/form/Combobox";

const REMINDER_OPTIONS = [
  { value: "none", label: "No reminder" },
  { value: "5m", label: "5 minutes before" },
  { value: "15m", label: "15 minutes before" },
  { value: "30m", label: "30 minutes before" },
  { value: "1h", label: "1 hour before" },
  { value: "1d", label: "1 day before" },
];

const CalendarSection = ({
  selectedDate,
  setSelectedDate,
  reminderType,
  setReminderType,
  isEditMode,
  editingFollowUp,
  selectedTime,
  setSelectedTime,
  timeSlots,
  isScheduleInPast,
  channelId,
  followUpName,
  setFollowUpName,
  enableLeadSelection,
  isJobActivity = false,
  selectedLead,
  setSelectedLead,
  loadingLeads,
  availableLeads,
  assignedTo,
  setAssignedTo,
  attendees,
  setAttendees,
  loadingUsers,
  availableUsers,
  setPendingAssignedTo,
  setShowReassignModal,
  leadData,
  errors = {},
  setErrors = () => {},
  notes,
  setNotes,
  statusId,
  setStatusId,
  restrictedEditMode = false,
  isReminderDisabled = false,
  currentLoggedInUserId,
  isAdminOrOwner = false,
}) => {
  const activityNameInputRef = React.useRef(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (activityNameInputRef.current) {
        activityNameInputRef.current.focus();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const isEditingExisting = !!(
    editingFollowUp && !editingFollowUp.isReschedule
  );

  // In edit mode filter available leads to only the lead being edited
  // For new activity from a specific lead, only show that lead initially unless it's deselected
  const filteredLeads = React.useMemo(() => {
    if (isEditingExisting && editingFollowUp) {
      const editLeadId =
        editingFollowUp.LeadId || editingFollowUp.leadId || editingFollowUp.id;
      if (editLeadId) {
        const found = availableLeads?.find(
          (l) => String(l.value) === String(editLeadId),
        );
        return found ? [found] : availableLeads;
      }
    }

    if (leadData) {
      const lId = leadData.id || leadData.leadId;
      if (lId && selectedLead && Array.isArray(selectedLead)) {
        if (Array.isArray(lId)) {
          // If bulk action array, filter leads to only show those that are STILL selected
          const lIdStrings = lId.map(id => String(id));
          const selectedLeadStrings = selectedLead.map(id => String(id));
          const filteredBulk = availableLeads?.filter(l => lIdStrings.includes(String(l.value)) && selectedLeadStrings.includes(String(l.value)));
          if (filteredBulk && filteredBulk.length > 0 && filteredBulk.length === lId.length) {
              return filteredBulk; // Only return filtered if all original selections are still there (to mimic "crossing it out shows others")
          }
        } else {
          if (selectedLead.includes(String(lId)) || selectedLead.includes(Number(lId))) {
            const found = availableLeads?.find(l => String(l.value) === String(lId));
            return found ? [found] : availableLeads;
          }
        }
      }
    }

    return availableLeads;
  }, [isEditingExisting, editingFollowUp, availableLeads, leadData, selectedLead]);

  const selectedAttendeeObjects = React.useMemo(() => {
    return (attendees || [])
      .map((attId) =>
        availableUsers?.find((user) => String(user.value) === String(attId)),
      )
      .filter(Boolean);
  }, [attendees, availableUsers]);

  const getLeadsDisplayValue = (selectedIds, leadsOptions) => {
    if (!selectedIds || selectedIds.length === 0) return "Search and select lead(s)";

    const selectedLeadsMap = [];
    leadsOptions?.forEach((lead) => {
      const isLeadIdSelected = selectedIds.includes(lead.value);
      const selectedContacts = lead.contacts?.filter(c => selectedIds.includes(`${lead.value}::${c.ContactId}`)) || [];

      if (isLeadIdSelected || selectedContacts.length > 0) {
        selectedLeadsMap.push({
          leadName: lead.label,
          contacts: selectedContacts.map((c) => c.FullName),
        });
      }
    });

    if (selectedLeadsMap.length === 0) {
      return `${selectedIds.length} items selected`;
    }

    if (selectedLeadsMap.length === 1) {
      const { leadName, contacts } = selectedLeadsMap[0];
      if (contacts.length > 0) {
        return `${leadName} (${contacts.join(", ")})`;
      }
      return leadName;
    }

    return `${selectedLeadsMap.length} leads selected`;
  };

  const selectedContactsList = React.useMemo(() => {
    const list = [];
    filteredLeads?.forEach((lead) => {
      lead.contacts?.forEach((contact) => {
        const contactKey = `${lead.value}::${contact.ContactId}`;
        if (selectedLead.includes(contactKey)) {
          list.push({
            contactId: contact.ContactId,
            fullName: contact.FullName,
            email: contact.Email,
            phone: contact.PhoneNumber,
            leadId: lead.value,
            leadName: lead.label,
            key: contactKey,
          });
        }
      });
    });
    return list;
  }, [selectedLead, filteredLeads]);

  const handleRemoveContact = (contactKey) => {
    if (restrictedEditMode) return;
    setSelectedLead((prev) => prev.filter((val) => String(val) !== String(contactKey)));
  };

  return (
    <div className="col-span-1 space-y-6 p-4 sm:p-6">
      {/* Follow-up Name input field */}
      <div className="mb-4">
        <Input
          ref={activityNameInputRef}
          autoFocus
          label="Activity Name"
          required
          value={followUpName}
          onChange={(e) => {
            setFollowUpName(e.target.value);
            if (errors?.followUpName) {
              setErrors((prev) => ({ ...prev, followUpName: null }));
            }
          }}
          maxLength={200}
          placeholder="Activity Name"
          className="w-full py-1"
          error={errors?.followUpName}
        />
        <div className="mt-1 flex items-center justify-between px-1">
          <span className="text-[10px] text-gray-400">Max 200 characters</span>
          <span className="text-[10px] font-semibold text-gray-400">
            {followUpName?.length || 0}/200
          </span>
        </div>
      </div>
      {/* Lead Selection (Conditional) — for Custom Activity (channelId 99) the lead is
          optional and can only be chosen at creation time (or via bulk lead-picking, e.g.
          the Activities list "+" flow). It's still shown when editing an existing Custom
          activity so the lead it's tied to is visible, matching Email/SMS activities —
          the lead itself remains immutable after creation regardless. Job Calendar Custom
          Activities are always lead-less, so this never applies to them. */}
      {!isJobActivity && (!(channelId == 99 || channelId === "99") || enableLeadSelection || editingFollowUp) && (
        <div className="mb-4">
          <MultiSelectHierarchicalFilterDropdown
            label={channelId == 99 || channelId === "99" ? "Lead(s) (optional)" : "Lead(s)"}
            required={!(channelId == 99 || channelId === "99")}
            filterKey="leads"
            options={filteredLeads?.map(lead => ({
              id: lead.value,
              label: lead.label,
              type: "stage",
              children: lead.contacts?.map(contact => ({
                id: `${lead.value}::${contact.ContactId}`,
                label: `${contact.FullName}${contact.Email ? ` (${contact.Email})` : ''}`,
                type: "substage"
              })) || []
            }))}
            selectedValues={selectedLead}
            onSelect={(key, val) => {
              setSelectedLead(val);
              if (errors?.selectedLead) {
                setErrors(prev => ({ ...prev, selectedLead: null }));
              }
            }}
            onClear={() => {
              if (!isEditingExisting) setSelectedLead([]);
            }}
            placeholder="Search and select lead(s)"
            usePortal={false}
            className="w-full"
            error={errors?.selectedLead}
            toggleChildren={true}
            showAllOption={false}
            getDisplayValue={(selectedValues) => getLeadsDisplayValue(selectedValues, filteredLeads)}
          />
          {selectedContactsList.length > 0 && (
            <div className="animate-in fade-in slide-in-from-top-1 mt-2 flex flex-wrap gap-1.5 duration-250">
              {selectedContactsList.map((contact) => (
                <span
                  key={contact.key}
                  className="bg-primary-50 dark:bg-primary-900/10 text-primary-700 dark:text-primary-300 border-primary-100 dark:border-primary-900/30 inline-flex items-center gap-1.5 rounded-full border py-1 pr-1.5 pl-2.5 text-xs font-semibold shadow-sm transition-all hover:scale-105"
                >
                  {contact.fullName || contact?.email}
                  {!restrictedEditMode && (
                    <button
                      type="button"
                      onClick={() => handleRemoveContact(contact.key)}
                      className="hover:bg-primary-100 dark:hover:bg-primary-800/50 rounded-full p-0.5 transition-colors"
                      title="Remove Contact"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Date Selection */}
      {!(channelId == 1 || channelId == 2) && (
        <div className="mb-4">
          <DatePicker
            label="Activity Date"
            required
            value={selectedDate}
            onChange={([date]) => {
              setSelectedDate(date);
              if (errors?.selectedDate) {
                setErrors((prev) => ({ ...prev, selectedDate: null }));
              }
            }}
            disabled={restrictedEditMode}
            placeholder="Choose activity date"
            options={{
              minDate: (selectedDate && !isNaN(new Date(selectedDate).getTime()) && new Date(selectedDate).getTime() < new Date().setHours(0, 0, 0, 0)) ? selectedDate : "today",
              dateFormat: "d/m/Y",
              disableMobile: true,
            }}
            error={errors?.selectedDate}
          />

          {/* Selected Date Display */}
          {selectedDate && (
            <Card className="bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 mt-4 p-3">
              <div className="text-primary-700 dark:text-primary-300 flex items-center gap-2">
                <CalendarDaysIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Scheduled: {formatDate(selectedDate)}
                </span>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Time Selection */}
      {!(channelId == 1 || channelId == 2) && (
        <div className="mb-4">
          <TimePicker
            label="Time"
            required
            value={selectedTime}
            onChange={(e) => {
              setSelectedTime(e.target.value);
              if (errors?.selectedTime) {
                setErrors((prev) => ({ ...prev, selectedTime: null }));
              }
            }}
            className="w-full"
            disabled={restrictedEditMode}
            error={errors?.selectedTime}
            min={(() => {
              if (selectedDate) {
                const today = new Date();
                const selDate = new Date(selectedDate);
                if (
                  selDate &&
                  !isNaN(selDate.getTime()) &&
                  selDate.toDateString() === today.toDateString()
                ) {
                  const currentHours = String(today.getHours()).padStart(
                    2,
                    "0",
                  );
                  const currentMinutes = String(today.getMinutes()).padStart(
                    2,
                    "0",
                  );
                  return `${currentHours}:${currentMinutes}`;
                }
              }
              return undefined;
            })()}
          />
        </div>
      )}

      {/* Reminder Selection */}
      <div>
        <label className="dark:text-dark-300 mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
          <BellIcon className="text-primary-600 h-4 w-4" /> Remind Me
        </label>
        <Listbox
          data={REMINDER_OPTIONS}
          value={REMINDER_OPTIONS.find((opt) => opt.value === reminderType)}
          onChange={(item) => setReminderType(item?.value || "none")}
          inputProps={{
            className: "w-full",
            disabled: restrictedEditMode || isReminderDisabled,
            onFocus: (e) => {
              const el = e.target;
              setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }
          }}
        />
      </div>

      {/* Assign To */}
      <div className="mb-4">
        <MultiSelectCheckboxDropdown
          label={
            <span className="flex items-center gap-1">
              Assign To <span className="text-red-500 font-bold">*</span>
            </span>
          }
          isMulti={false}
          options={availableUsers?.map((user) => ({
            value: user.value,
            label: user.label,
            designation: user.designations || user.designationName || user.roleName,
          }))}
          value={assignedTo}
          onChange={(val) => {
            if (errors?.assignedTo) {
              setErrors((prev) => ({ ...prev, assignedTo: null }));
            }
            setAssignedTo(val || "");
          }}
          placeholder={loadingUsers ? "Loading..." : "Select Assignee"}
          disabled={
            loadingUsers ||
            (restrictedEditMode && !editingFollowUp?.isReschedule)
          }
          usePortal={false}
          className="w-full"
          error={errors?.assignedTo}
        />
      </div>

      {/* Attendees */}
      <div className="mb-4">
        <MultiSelectCheckboxDropdown
          label="Attendee(s)"
          options={availableUsers
            ?.filter(
              (user) =>
                isAdminOrOwner ||
                !currentLoggedInUserId ||
                String(user.value).toLowerCase() !==
                  String(currentLoggedInUserId).toLowerCase(),
            )
            ?.map((user) => ({
              value: user.value,
              label: user.label,
              designation: user.designations || user.designationName || user.roleName,
            }))}
          value={attendees}
          onChange={setAttendees}
          placeholder="Select attendees"
          disabled={
            loadingUsers ||
            (restrictedEditMode && !editingFollowUp?.isReschedule)
          }
          usePortal={false}
          className="w-full"
        />
        {selectedAttendeeObjects.length > 0 && (
          <div className="animate-in fade-in slide-in-from-top-1 mt-2 flex flex-wrap gap-1.5 duration-250">
            {selectedAttendeeObjects.map((user) => (
              <span
                key={user.value}
                className="bg-primary-50 dark:bg-primary-900/10 text-primary-700 dark:text-primary-300 border-primary-100 dark:border-primary-900/30 inline-flex items-center gap-1.5 rounded-full border py-1 pr-1.5 pl-2.5 text-xs font-semibold shadow-sm transition-all hover:scale-105"
              >
                {user.label}
                {(!restrictedEditMode || editingFollowUp?.isReschedule) && (
                  <button
                    type="button"
                    onClick={() =>
                      setAttendees(
                        attendees.filter(
                          (id) => String(id) !== String(user.value),
                        ),
                      )
                    }
                    className="hover:bg-primary-100 dark:hover:bg-primary-800/50 rounded-full p-0.5 transition-colors"
                    title="Remove Attendee"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status Selection (Only show in Edit/Reschedule mode) */}
      {editingFollowUp && (
        <div className="mb-4">
          <Listbox
            label={
              <span className="flex items-center gap-1">
                Status <span className="text-red-500 font-bold">*</span>
              </span>
            }
            data={ACTIVITY_STATUS?.map(status => ({ value: status.id, label: status.name })) || []}
            placeholder="Select Status"
            value={
              ACTIVITY_STATUS?.map(status => ({ value: status.id, label: status.name }))
                .find((s) => s.value === statusId) || null
            }
            onChange={(item) => {
              setStatusId(item?.value || "");
              if (errors?.statusId) {
                setErrors((prev) => ({ ...prev, statusId: null }));
              }
            }}
            inputProps={{
              className: "w-full",
              onFocus: (e) => {
                const el = e.target;
                setTimeout(() => {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }
            }}
            error={errors?.statusId}
          />
        </div>
      )}

      {/* Remarks */}
      <div className="mb-4">
        <label className="dark:text-dark-300 mb-2 block text-sm font-medium text-gray-700">
          Remarks (General Activity Comments)
        </label>
        <textarea
          className={`dark:bg-dark-850/80 focus:ring-primary-500 dark:border-dark-600 dark:placeholder:text-dark-400 flex min-h-[80px] w-full rounded-xl border bg-white/80 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:outline-none dark:text-white ${
            errors?.notes
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-200"
          }`}
          value={notes || ""}
          onChange={(e) => {
            setNotes(e.target.value);
            if (errors?.notes) {
              setErrors((prev) => ({ ...prev, notes: null }));
            }
          }}
          onFocus={(e) => {
            const el = e.target;
            setTimeout(() => {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300); // delay to wait for keyboard
          }}
          maxLength={500}
          placeholder="Enter general remarks..."
        />
        <div className="mt-1 flex items-center justify-between px-1">
          {errors?.notes ? (
            <span className="text-xs font-medium text-red-500">
              {errors.notes}
            </span>
          ) : (
            <span className="text-[10px] text-gray-400">
              Max 500 characters
            </span>
          )}
          <span className="text-[10px] font-semibold text-gray-400">
            {notes?.length || 0}/500
          </span>
        </div>
      </div>
    </div>
  );
};

export default CalendarSection;
